/**
 * Helpers dos templates oficiais (Meta/WhatsApp).
 *
 * Mantêm a mesma semântica que o backend espera em POST /messages-template:
 * variables[componentType][indice] = { value, buttonIndex }.
 */

const PLACEHOLDER_REGEX = /{{\s*(\d+)\s*}}/g;

// A Meta entrega os componentes em ordem arbitrária (e o banco guarda assim),
// mas o cliente sempre vê nesta ordem.
const TYPE_ORDER = { HEADER: 0, BODY: 1, FOOTER: 2, BUTTONS: 3 };

const MEDIA_FORMATS = ["IMAGE", "VIDEO", "DOCUMENT"];

export const componentType = (component) =>
  String(component?.type || "").toUpperCase();

export const isMediaHeader = (component) =>
  componentType(component) === "HEADER" &&
  MEDIA_FORMATS.includes(String(component?.format || "").toUpperCase());

/**
 * Os componentes chegam como array pela API, mas em alguns pontos o registro é
 * persistido como JSON, então normalizamos os dois formatos.
 */
export const parseComponents = (template) => {
  const components = template?.components;
  if (!components) return [];
  if (Array.isArray(components)) return components;

  try {
    const parsed = JSON.parse(components);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
};

/** Cabeçalho, corpo, rodapé e botões — nessa ordem, como o cliente recebe. */
export const orderedComponents = (components) =>
  [...(components || [])].sort(
    (a, b) =>
      (TYPE_ORDER[componentType(a)] ?? 99) - (TYPE_ORDER[componentType(b)] ?? 99)
  );

export const findComponent = (components, type) =>
  (components || []).find((component) => componentType(component) === type);

const parseJson = (value) => {
  if (!value) return null;
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch (err) {
    return null;
  }
};

export const templateButtons = (components) =>
  parseJson(findComponent(components, "BUTTONS")?.buttons) || [];

/** Texto do template na ordem correta, sem substituir as variáveis. */
export const templateText = (components) =>
  orderedComponents(components)
    .filter((component) => componentType(component) !== "BUTTONS")
    .map((component) => component?.text)
    .filter(Boolean)
    .join("\n");

/**
 * Troca {{1}}, {{2}}... pelos valores preenchidos daquele componente.
 * Sem valor, mantém o placeholder (na pré-visualização) ou remove (no envio).
 */
export const fillPlaceholders = (text, componentValues, { keepEmpty = true } = {}) =>
  String(text || "").replace(PLACEHOLDER_REGEX, (placeholder, position) => {
    const value = componentValues?.[Number(position) - 1]?.value;
    if (value) return value;
    return keepEmpty ? placeholder : "";
  });

/**
 * Exemplos que a Meta devolve junto do template ("header_text": ["Ana"],
 * "body_text": [["Ana", "10/05"]]). Usados só para orientar quem preenche.
 */
const exampleValues = (component) => {
  const example = parseJson(component?.example);
  if (!example) return [];

  const raw =
    example.body_text || example.header_text || example.button_text || [];
  const flat = Array.isArray(raw[0]) ? raw[0] : raw;

  return Array.isArray(flat) ? flat.filter((item) => typeof item === "string") : [];
};

/**
 * Extrai as variáveis ({{1}}, {{2}}, ...) agrupadas por tipo de componente.
 * Cada variável carrega um rótulo amigável e o exemplo da Meta, quando houver.
 */
export const extractVariablesByComponent = (components) => {
  const variables = { header: [], body: [], footer: [], buttons: [] };

  orderedComponents(components).forEach((component) => {
    const type = componentType(component).toLowerCase();
    if (!variables[type]) return;

    const text = component?.text || "";
    const examples = exampleValues(component);

    if (isMediaHeader(component)) {
      const format = String(component.format).toUpperCase();
      const isImage = format === "IMAGE";
      variables.header.push({
        type: format.toLowerCase(),
        label: isImage ? "URL da imagem" : format === "VIDEO" ? "URL do vídeo" : "URL do documento",
        prompt: `Insira a URL ${isImage ? "da imagem" : format === "VIDEO" ? "do vídeo" : "do documento"}`,
      });
      return;
    }

    if (type === "buttons") {
      (parseJson(component?.buttons) || []).forEach((button, index) => {
        if (!button?.example) return;
        variables.buttons.push({
          type: button.type,
          label: `Botão ${index + 1}${button.text ? ` — ${button.text}` : ""}`,
          prompt: button.example,
          example: Array.isArray(button.example) ? button.example[0] : button.example,
          index,
        });
      });
      return;
    }

    // A posição no array é o número do placeholder ({{2}} → índice 1), que é a
    // ordem em que a Meta espera os parâmetros — o texto pode citá-los fora de
    // ordem ou repetir o mesmo número.
    const numbers = new Set();
    let match;
    PLACEHOLDER_REGEX.lastIndex = 0;
    while ((match = PLACEHOLDER_REGEX.exec(text)) !== null) {
      numbers.add(Number(match[1]));
    }

    const total = Math.max(0, ...numbers);
    for (let number = 1; number <= total; number += 1) {
      variables[type][number - 1] = {
        type: "text",
        label: `Variável ${number}`,
        prompt: `{{${number}}}`,
        example: examples[number - 1],
      };
    }
  });

  return variables;
};

/**
 * Retrato do template no momento do envio: cabeçalho, corpo, rodapé e botões
 * com as variáveis já substituídas. É o que a pré-visualização desenha e o que
 * fica gravado na mensagem, para o chat exibir exatamente igual.
 */
export const buildTemplateSnapshot = (components, values, { keepEmpty = false } = {}) => {
  const header = findComponent(components, "HEADER");
  const body = findComponent(components, "BODY");
  const footer = findComponent(components, "FOOTER");

  return {
    header: header
      ? {
          format: String(header.format || "TEXT").toUpperCase(),
          text: isMediaHeader(header)
            ? ""
            : fillPlaceholders(header.text, values?.header, { keepEmpty }),
          mediaUrl: isMediaHeader(header) ? values?.header?.[0]?.value || "" : "",
        }
      : null,
    body: fillPlaceholders(body?.text, values?.body, { keepEmpty }),
    footer: footer?.text || "",
    buttons: templateButtons(components).map((button) => ({
      type: button?.type,
      text: button?.text,
    })),
  };
};

/**
 * Texto final da mensagem, na ordem em que o cliente recebe. É o que fica
 * gravado no histórico do ticket (o backend anexa os botões depois).
 * Cabeçalho de mídia entra como URL na primeira linha, que é o formato que o
 * componente de template da lista de mensagens sabe renderizar.
 */
export const buildBodyToSave = (components, values) => {
  const partes = [];

  orderedComponents(components).forEach((component) => {
    const type = componentType(component);
    if (type === "BUTTONS") return;

    if (isMediaHeader(component)) {
      const url = values?.header?.[0]?.value;
      if (url) partes.push(url);
      return;
    }

    const texto = fillPlaceholders(component?.text, values?.[type.toLowerCase()], {
      keepEmpty: false,
    });

    if (texto.trim()) partes.push(texto);
  });

  return partes.join("\n");
};
